import {
  Box,
  Heading,
  Button,
  Badge,
  Text,
  HStack,
  VStack,
  Code,
  Tooltip,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuditLogs } from '@/hooks/useAdminData'
import {
  FiActivity,
  FiCalendar,
  FiClipboard,
  FiEye,
  FiGitCommit,
  FiMoreHorizontal,
  FiUser,
} from 'react-icons/fi'
import { DataTable } from '@/components/common/DataTable'
import {
  ColumnConfig,
  FilterConfig,
  PaginationConfig,
  ActionButton,
} from '@/components/common/DataTable/types'
import { formatDate, formatTime } from '@/utils/formatUtils'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { useToast } from '@/hooks/useToast'
import { triggerBrowserDownload } from '@/services/admin/baseService'
import { auditService } from '@/services/admin/auditService'
import { OrderStateEventsPanel } from '@/components/audit/OrderStateEventsPanel'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import { MainnetOnlyNotice } from '@/components/common/MainnetOnlyNotice'
import { useAdminTabQuery } from '@/navigation/adminTabQuery'

const AUDIT_LOG_TABS = ['admin_audit_logs', 'order_state_events'] as const

export function AuditLogsPage() {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const { success: showToastSuccess, error: showToastError } = useToast()
  const [page, setPage] = useState(1)
  const [action, setAction] = useState('')
  const [adminUserId, setAdminUserId] = useState('')
  const { activeTabIndex: tabIndex, handleTabChange } = useAdminTabQuery({
    tabs: AUDIT_LOG_TABS,
    defaultTab: 'admin_audit_logs',
  })
  const limit = 50
  const isMainnet = currentEnv === 'mainnet'

  const { data, isLoading, error, refetch, isFetching } = useAuditLogs(
    {
      page,
      limit,
      action,
      admin_user_id: adminUserId,
    },
    { enabled: isMainnet && tabIndex === 0 }
  )

  const getActionColor = (action: string) => {
    switch (action.toLowerCase()) {
      case 'login':
        return 'green'
      case 'logout':
        return 'blue'
      case 'create':
        return 'purple'
      case 'update':
        return 'yellow'
      case 'delete':
        return 'red'
      case 'suspend':
      case 'ban':
        return 'red'
      case 'activate':
      case 'approve':
        return 'green'
      case 'view':
      case 'read':
        return 'gray'
      default:
        return 'blue'
    }
  }

  const formatDetails = (details: unknown): string => {
    const stringifyValue = (value: unknown) => {
      if (value === null || value === undefined) {
        return t('common.na')
      }
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value)
        } catch {
          return String(value)
        }
      }
      return String(value)
    }

    if (typeof details === 'string') {
      try {
        const parsed = JSON.parse(details)
        if (parsed && typeof parsed === 'object') {
          return Object.entries(parsed)
            .map(([key, value]) => `${key}: ${stringifyValue(value)}`)
            .join(', ')
        }
        return stringifyValue(parsed)
      } catch {
        return details || t('common.na')
      }
    }

    if (details && typeof details === 'object') {
      if (Array.isArray(details)) {
        return details.map(item => stringifyValue(item)).join(', ')
      }
      return Object.entries(details as Record<string, unknown>)
        .map(([key, value]) => `${key}: ${stringifyValue(value)}`)
        .join(', ')
    }

    return stringifyValue(details)
  }

  // Define column configuration for DataTable
  const columns: ColumnConfig<any>[] = [
    {
      key: 'created_at',
      title: t('auditLogs.columns.timestamp'),
      width: '15%',
      render: value => (
        <HStack spacing={2}>
          <Icon as={FiCalendar} color="gray.500" />
          <Box>
            <Text fontSize="sm" fontWeight="medium">
              {value ? formatDate(value) : t('common.na')}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {value ? formatTime(value) : ''}
            </Text>
          </Box>
        </HStack>
      ),
    },
    {
      key: 'username',
      title: t('auditLogs.columns.user'),
      width: '15%',
      render: (value, row) => (
        <HStack spacing={2}>
          <Icon as={FiUser} color="gray.500" />
          <Box>
            <Text fontSize="sm" fontWeight="medium">
              {value ?? t('common.pending')}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {row.role_name ?? t('common.pending')}
            </Text>
          </Box>
        </HStack>
      ),
    },
    {
      key: 'action',
      title: t('auditLogs.columns.action'),
      width: '12%',
      render: value => (
        <Badge colorScheme={getActionColor(value)} size="sm">
          {value?.toUpperCase() ?? t('common.pending')}
        </Badge>
      ),
    },
    {
      key: 'resource_type',
      title: t('auditLogs.columns.resource'),
      width: '15%',
      render: (value, row) => (
        <Box>
          <Text fontSize="sm">{value ?? 'System'}</Text>
          {row.resource_id && (
            <Code fontSize="xs" color="gray.500">
              {row.resource_id}
            </Code>
          )}
        </Box>
      ),
    },
    {
      key: 'details',
      title: t('auditLogs.columns.details'),
      width: '35%',
      render: value => (
        <Tooltip label={formatDetails(value)} placement="top">
          <Text fontSize="xs" color="gray.600" noOfLines={2}>
            {formatDetails(value)}
          </Text>
        </Tooltip>
      ),
    },
  ]

  // Define filter configuration for DataTable
  const filters: FilterConfig[] = [
    {
      key: 'search',
      type: 'text',
      label: t('auditLogs.searchPlaceholder'),
      placeholder: t('auditLogs.searchPlaceholder'),
      width: '300px',
    },
    {
      key: 'action',
      type: 'select',
      label: t('auditLogs.filterByAction'),
      placeholder: t('auditLogs.filterByAction'),
      width: '200px',
      options: [
        { value: 'login', label: t('auditLogs.actions.login') },
        { value: 'logout', label: t('auditLogs.actions.logout') },
        { value: 'create', label: t('auditLogs.actions.create') },
        { value: 'update', label: t('auditLogs.actions.update') },
        { value: 'delete', label: t('auditLogs.actions.delete') },
        { value: 'view', label: t('auditLogs.actions.view') },
        { value: 'approve', label: t('auditLogs.actions.approve') },
        { value: 'suspend', label: t('auditLogs.actions.suspend') },
        { value: 'activate', label: t('auditLogs.actions.activate') },
      ],
    },
    {
      key: 'admin_user_id',
      type: 'text',
      label: t('auditLogs.filterByAdminUser'),
      placeholder: t('auditLogs.filterByAdminUser'),
      width: '300px',
    },
  ]

  // Define pagination configuration
  const paginationConfig: PaginationConfig = {
    page,
    pageSize: limit,
    total: data?.pagination?.total || 0,
    pageSizeOptions: [25, 50, 100],
  }

  // Get actions for each row
  const getRowActions = (_log: any): ActionButton[] => {
    return [
      {
        label: '',
        icon: <FiEye />,
        tooltip: t('auditLogs.actions.view'),
        onClick: () => {},
        size: 'xs',
        variant: 'ghost',
      },
      {
        label: '',
        icon: <FiMoreHorizontal />,
        tooltip: t('auditLogs.actions.context'),
        onClick: () => {},
        size: 'xs',
        variant: 'ghost',
      },
    ]
  }

  // Handle filter changes
  const handleFiltersChange = (newFilters: Record<string, any>) => {
    setAction(newFilters.action || '')
    setAdminUserId(newFilters.admin_user_id || '')
    setPage(1)
  }

  // Handle pagination changes
  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (_newSize: number) => {
    // Page size is fixed.
  }

  // Handle export
  const handleExport = async () => {
    try {
      const { blob, filename } = await auditService.exportCsv(
        {
          page,
          limit,
          action,
          admin_user_id: adminUserId,
        },
        currentEnv
      )
      triggerBrowserDownload(blob, filename)
      showToastSuccess({
        title: t('common.exportCsv'),
        description: t('common.export.downloaded'),
      })
    } catch (error) {
      showToastError({
        title: t('common.exportCsv'),
        description:
          error instanceof Error ? error.message : t('common.operationFailed'),
      })
    }
  }

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between" align="flex-start">
          <Box>
            <Heading size="lg" mb={2}>
              <Icon as={FiActivity} mr={3} />
              {t('auditLogs.title')}
            </Heading>
            <Text color="gray.600">{t('auditLogs.subtitle')}</Text>
          </Box>
          {tabIndex === 0 && isMainnet && (
            <RefreshIconButton
              label={t('common.refresh')}
              tooltipPlacement="left"
              size="md"
              onClick={() => refetch()}
              isLoading={isFetching}
            />
          )}
        </HStack>

        <Tabs
          index={tabIndex}
          onChange={handleTabChange}
          variant="enclosed"
          colorScheme="admin"
          isLazy
          lazyBehavior="keepMounted"
        >
          <TabList
            overflowX={{ base: 'auto', md: 'visible' }}
            overflowY="hidden"
          >
            <Tab
              aria-label={t('auditLogs.tabs.adminAuditLogs')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip label={t('auditLogs.tabs.adminAuditLogs')} hasArrow>
                <Box display="flex" alignItems="center">
                  <Icon as={FiClipboard} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
            <Tab
              aria-label={t('auditLogs.tabs.orderStateEvents')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip label={t('auditLogs.tabs.orderStateEvents')} hasArrow>
                <Box display="flex" alignItems="center">
                  <Icon as={FiGitCommit} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0} pt={4}>
              {isMainnet ? (
                <Box>
                  <Box mb={4}>
                    <HStack spacing={3}>
                      <Button colorScheme="purple">
                        {t('auditLogs.advancedSearch')}
                      </Button>
                    </HStack>
                  </Box>

                  <DataTable<any>
                    data={data?.data || []}
                    loading={isLoading}
                    error={error}
                    columns={columns}
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    showFilterBar={true}
                    sortable={true}
                    pagination={paginationConfig}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    actions={getRowActions}
                    actionsColumnWidth="6%"
                    size="sm"
                    emptyMessage={t('auditLogs.noLogs')}
                    showExport={true}
                    onExport={handleExport}
                  />
                </Box>
              ) : (
                <MainnetOnlyNotice />
              )}
            </TabPanel>
            <TabPanel px={0} pt={4}>
              <OrderStateEventsPanel isActive={tabIndex === 1} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Box>
  )
}
